import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { useCurrentUser } from '../auth';
import type {
  BulkContactCommitResult,
  BulkContactCommitRow,
  BulkContactParseResponse,
  ResolvedBulkContactRow,
  SuburbResolution,
} from '../../types/contactBulkImport';
import { commitBulkContacts, parseBulkContacts } from './bulkImportApi';
import {
  downloadBulkContactTemplate,
  parseCsvText,
  parseFileToRawRows,
  type RawRowsResult,
} from './bulkImportParse';

// Bulk Contact Upload — full-page flow (§5). Upload an xlsx/CSV (or paste
// CSV), preview the parsed + resolved rows, confirm any fuzzy/ambiguous
// suburbs, then commit. Admin-only.

type Step = 'upload' | 'preview' | 'done';

const SKIP = '__skip__';

function suburbKey(rowNumber: number, idx: number): string {
  return `${rowNumber}:${idx}`;
}

function rowName(cleaned: ResolvedBulkContactRow['cleaned']): string {
  return [cleaned.title, cleaned.firstName, cleaned.lastName]
    .filter((p): p is string => Boolean(p))
    .join(' ');
}

function isSuburbUnresolved(s: SuburbResolution, skipped: Set<string>, key: string): boolean {
  return (
    (s.status === 'fuzzy' || s.status === 'ambiguous') &&
    s.selected === null &&
    !skipped.has(key)
  );
}

function rowHasUnresolved(row: ResolvedBulkContactRow, skipped: Set<string>): boolean {
  if (row.status === 'error') return false;
  return row.resolved.suburbs.some((s, idx) =>
    isSuburbUnresolved(s, skipped, suburbKey(row.rowNumber, idx)),
  );
}

const STATUS_BADGE: Record<ResolvedBulkContactRow['status'], { glyph: string; cls: string; label: string }> = {
  ready: { glyph: '✓', cls: 'bg-emerald-100 text-emerald-700', label: 'Ready' },
  needs_resolution: { glyph: '⚠', cls: 'bg-amber-100 text-amber-800', label: 'Needs resolution' },
  error: { glyph: '✗', cls: 'bg-red-100 text-red-700', label: 'Error' },
};

function StatusBadge({ status }: { status: ResolvedBulkContactRow['status'] }) {
  const b = STATUS_BADGE[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${b.cls}`}
      title={b.label}
    >
      <span aria-hidden>{b.glyph}</span>
      {b.label}
    </span>
  );
}

function SummaryPanel({ summary }: { summary: BulkContactParseResponse['summary'] }) {
  const cell = (label: string, value: number, cls = '') => (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cell('Total rows', summary.totalRows)}
      {cell('Ready', summary.ready, 'text-emerald-700')}
      {cell('Need resolution', summary.needsResolution, summary.needsResolution > 0 ? 'text-amber-700' : '')}
      {cell('Errors', summary.errors, summary.errors > 0 ? 'text-red-700' : '')}
      {cell('Contacts to create', summary.contactsToCreate)}
      {cell('Contacts to update', summary.contactsToUpdate)}
      {cell('Links to create', summary.linksToCreate)}
      {cell('Links to update', summary.linksToUpdate)}
    </div>
  );
}

function SuburbCell({
  row,
  idx,
  skipped,
  onChoose,
}: {
  row: ResolvedBulkContactRow;
  idx: number;
  skipped: Set<string>;
  onChoose: (rowNumber: number, idx: number, value: string) => void;
}) {
  const s = row.resolved.suburbs[idx]!;
  const key = suburbKey(row.rowNumber, idx);

  if (s.status === 'exact' && s.selected) {
    return (
      <span className="inline-block rounded bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs">
        {s.selected.name}
      </span>
    );
  }
  if (s.status === 'unmatched') {
    return (
      <span className="inline-block rounded bg-red-50 text-red-700 px-2 py-0.5 text-xs">
        {s.input} (no match)
      </span>
    );
  }

  // fuzzy / ambiguous — interactive dropdown.
  const value = skipped.has(key) ? SKIP : s.selected ? s.selected.id : '';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] text-amber-800">
        {s.input} <span className="text-slate-400">({s.status})</span>
      </span>
      <select
        className="rounded border border-slate-300 px-1.5 py-0.5 text-xs"
        value={value}
        onChange={(e) => onChoose(row.rowNumber, idx, e.target.value)}
      >
        <option value="">Choose…</option>
        {s.candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {s.status === 'fuzzy' ? ` (${Math.round(c.score * 100)}%)` : ''}
          </option>
        ))}
        <option value={SKIP}>Skip this location</option>
      </select>
    </div>
  );
}

export function BulkContactUpload() {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('upload');
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [parseResponse, setParseResponse] = useState<BulkContactParseResponse | null>(null);
  const [rows, setRows] = useState<ResolvedBulkContactRow[]>([]);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<BulkContactCommitResult | null>(null);

  const unresolvedCount = useMemo(
    () => rows.filter((r) => rowHasUnresolved(r, skipped)).length,
    [rows, skipped],
  );
  const committableCount = useMemo(
    () => rows.filter((r) => r.status !== 'error').length,
    [rows],
  );
  const canCommit = !committing && unresolvedCount === 0 && committableCount > 0;

  if (user.role !== 'admin') {
    return (
      <div className="text-sm text-red-600">
        Bulk upload is admin-only.{' '}
        <Link to="/contacts" className="underline">Back to contacts</Link>
      </div>
    );
  }

  async function runParse(raw: RawRowsResult) {
    if (raw.missingRequiredColumns.length > 0) {
      setError(`File is missing required column(s): ${raw.missingRequiredColumns.join(', ')}.`);
      return;
    }
    if (raw.rows.length === 0) {
      setError('No data rows found in the file.');
      return;
    }
    setParsing(true);
    setError(null);
    try {
      const resp = await parseBulkContacts(raw.rows);
      setParseResponse(resp);
      setRows(resp.rows);
      setSkipped(new Set());
      setStep('preview');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setParsing(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const raw = await parseFileToRawRows(file);
      await runParse(raw);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      e.target.value = '';
    }
  }

  async function onParsePastedCsv() {
    setError(null);
    try {
      await runParse(parseCsvText(csvText));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function chooseSuburb(rowNumber: number, idx: number, value: string) {
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowNumber !== rowNumber) return r;
        const suburbs = r.resolved.suburbs.map((s, i) => {
          if (i !== idx) return s;
          if (value === '' || value === SKIP) return { ...s, selected: null };
          const cand = s.candidates.find((c) => c.id === value);
          return cand ? { ...s, selected: { id: cand.id, name: cand.name } } : s;
        });
        return { ...r, resolved: { ...r.resolved, suburbs } };
      }),
    );
    setSkipped((prev) => {
      const next = new Set(prev);
      const key = suburbKey(rowNumber, idx);
      if (value === SKIP) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  async function commit() {
    const payload: BulkContactCommitRow[] = rows
      .filter((r) => r.status !== 'error')
      .map((r) => ({
        rowNumber: r.rowNumber,
        raw: r.raw,
        selectedSuburbAccountIds: r.resolved.suburbs
          .filter((s) => s.selected !== null)
          .map((s) => s.selected!.id),
      }));
    setCommitting(true);
    setError(null);
    try {
      const res = await commitBulkContacts(payload);
      setResult(res);
      setStep('done');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['contacts'] }),
        qc.invalidateQueries({ queryKey: ['accounts'] }),
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCommitting(false);
    }
  }

  function startOver() {
    setStep('upload');
    setParseResponse(null);
    setRows([]);
    setSkipped(new Set());
    setResult(null);
    setError(null);
    setCsvText('');
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Bulk upload contacts</h1>
          <p className="text-sm text-slate-600">
            Upload an Excel/CSV of contacts. Each row links a contact to a Brand,
            optional Group, and one or more location suburbs.
          </p>
        </div>
        <Link to="/contacts">
          <Button variant="outline">Back to contacts</Button>
        </Link>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
      )}

      {step === 'upload' && (
        <section className="bg-white rounded-md border border-slate-200 p-6 space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Upload file (.xlsx, .xls, .csv)</label>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} disabled={parsing} />
          </div>

          <div className="text-sm text-slate-500">
            Need the column layout?{' '}
            <button type="button" className="text-slate-700 underline" onClick={downloadBulkContactTemplate}>
              Download the template
            </button>
            .
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-700">Or paste CSV text</summary>
            <div className="mt-3 space-y-2">
              <textarea
                className="w-full h-40 rounded border border-slate-300 px-3 py-2 font-mono text-xs"
                placeholder="Paste CSV including the header row…"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                disabled={parsing}
              />
              <Button onClick={onParsePastedCsv} disabled={parsing || csvText.trim() === ''}>
                Parse pasted CSV
              </Button>
            </div>
          </details>

          {parsing && <CenteredSpinner />}
        </section>
      )}

      {step === 'preview' && parseResponse && (
        <div className="space-y-5">
          <SummaryPanel summary={parseResponse.summary} />

          {unresolvedCount > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              {unresolvedCount} row(s) still need a location chosen (or skipped) before you can commit.
            </div>
          )}
          {parseResponse.summary.errors > 0 && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {parseResponse.summary.errors} row(s) have errors and will be skipped. Fix the file and
              re-upload to include them.
            </div>
          )}

          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Brand</th>
                  <th className="px-3 py-2 font-medium">Group</th>
                  <th className="px-3 py-2 font-medium">Suburbs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.rowNumber} className="align-top">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <StatusBadge status={r.status} />
                      {r.resolved.contact && (
                        <div className="mt-1 text-[11px] text-slate-400">{r.resolved.contact.action}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.cleaned.email || '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{rowName(r.cleaned) || '—'}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {r.resolved.brand?.name ?? <span className="text-red-600">{r.cleaned.brand || '—'}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.resolved.group?.name ?? '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {r.resolved.suburbs.length === 0 ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          r.resolved.suburbs.map((_s, i) => (
                            <SuburbCell key={i} row={r} idx={i} skipped={skipped} onChoose={chooseSuburb} />
                          ))
                        )}
                      </div>
                      {(r.errors.length > 0 || r.warnings.length > 0) && (
                        <ul className="mt-1 space-y-0.5">
                          {r.errors.map((e, i) => (
                            <li key={`e${i}`} className="text-[11px] text-red-600">{e}</li>
                          ))}
                          {r.warnings.map((w, i) => (
                            <li key={`w${i}`} className="text-[11px] text-amber-700">{w}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={commit} disabled={!canCommit}>
              {committing ? 'Importing…' : `Commit ${committableCount} row(s)`}
            </Button>
            <Button variant="outline" onClick={startOver} disabled={committing}>
              Upload a different file
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <section className="bg-emerald-50 border border-emerald-200 rounded-md p-6 space-y-3">
          <h2 className="text-lg font-semibold text-emerald-900">Import complete</h2>
          <p className="text-sm text-emerald-900">
            Created {result.created} contact(s), updated {result.updated}. Created{' '}
            {result.linksCreated} link(s), updated {result.linksUpdated}. Skipped {result.skipped} row(s).
          </p>
          {result.errors.length > 0 && (
            <details className="text-sm text-red-800" open>
              <summary>Row errors ({result.errors.length})</summary>
              <ul className="list-disc pl-5 mt-1">
                {result.errors.map((e, i) => (
                  <li key={i}>
                    Row {e.rowNumber} ({e.email || 'no email'}): {e.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={startOver}>
              Upload another file
            </Button>
            <Link to="/contacts" className="text-sm underline">Back to contacts</Link>
          </div>
        </section>
      )}
    </div>
  );
}
